import * as d3 from "d3";
import dayjs from "dayjs";
import { MutableRefObject, useRef } from "react";
import { Entity } from "../../../models/viz/Entity";
import { MediaSource } from "../../../models/media/MediaSource";
import { distributeRowData } from "../../../utilities/RowDataUtils";
import { DateFormats } from "../../../utilities/dates";
import { FragmentsMap } from "../../playback/components/useLoadFragments";
import { TimelineEntityVm } from "./TimelineEntityVm";

export const rowCount = 4;
export const topLabelHeight = 30;
export const spriteHeight = 30;
export const interSpriteSpacing = 10;
export const rowHeight = spriteHeight + interSpriteSpacing;
export const spriteFieldHeight = rowCount * rowHeight + interSpriteSpacing;
export const totalHeight = topLabelHeight + spriteFieldHeight + rowHeight;

function useTimeline(
    fragmentsMap: MutableRefObject<FragmentsMap>,
    isShowingDebugUi: boolean,
    media: MediaSource,
    timelineRef: MutableRefObject<SVGSVGElement | undefined>,
    totalWidth: MutableRefObject<number>,
) {
    const domainOffset = useRef(0);
    const secondsPerPixel = useRef(1.6); // Zoom
    const xScale = useRef<d3.ScaleTime<number, number, never> | undefined>(undefined);
    const yScale = useRef<d3.ScaleLinear<number, number, never> | undefined>(undefined);
    const entities = useRef<TimelineEntityVm[]>(undefined);

    function setTimelineAttributes() {
        d3.select(timelineRef.current)
            .attr("class", "timeline-d3-chart")
            .attr("width", totalWidth.current)
            .attr("height", totalHeight);
    }

    function drawTimeline(centrePointOverride?: number) {
        xScale.current = d3
            .scaleTime()
            .domain(calculateDomain(centrePointOverride))
            .range([1, totalWidth.current - 1])
            .clamp(true);
        yScale.current = d3.scaleLinear().rangeRound([0, rowHeight]);

        drawSprites(entities.current);
        drawBackgroundGrid();
    }

    function drawSprites(entities: TimelineEntityVm[]) {
        const timeline = d3.select(timelineRef.current);
        const spriteTransform = (d: TimelineEntityVm) => {
            const x = xScale.current(d.startsAt);
            const y = yScale.current(d.dataRow) + interSpriteSpacing + topLabelHeight;
            return `translate(${x}, ${y})`;
        };

        timeline.selectAll(".sprite").remove();

        timeline
            .selectAll("chart")
            .data(entities)
            .enter()
            .insert("rect", ":first-child")
            .attr("class", "sprite")
            // Conditionally add class to empty fragment groups (if debug mode is on)
            .classed(
                "sprite-debug",
                (entity, _, __) => isShowingDebugUi && fragmentsMap.current[entity.sourceObject.id]?.length == 0,
            )
            .attr("transform", spriteTransform)
            .attr("height", spriteHeight)
            .attr("width", (d: TimelineEntityVm) => Math.max(0, xScale.current(d.endsAt) - xScale.current(d.startsAt)))
            .attr("fragmentGroupId", (d) => d.sourceObject.id);
    }

    function drawBackgroundGrid() {
        const timeline = d3.select(timelineRef.current);
        timeline.select("g.grid").remove();

        const xAxis = d3.axisBottom<Date>(xScale.current).tickFormat(formatTickLabels);
        const xAxisGenerator = timeline
            .insert("g", ":first-child")
            .attr("class", "grid")
            .attr("transform", `translate(0, ${totalHeight - rowHeight})`)
            .call(xAxis);

        xAxisGenerator.selectAll(".tick line").attr("y1", 25).attr("y2", -spriteFieldHeight);
        xAxisGenerator.selectAll(".tick text").attr("y", 8).attr("dx", 2).attr("text-anchor", "start");

        // Background representing area of timeline with video
        const startsAtPoint = xScale.current(media.startsAt);
        const endsAtPoint = xScale.current(media.startsAt + media.duration);
        timeline
            .select("g.grid")
            .insert("rect", ":first-child")
            .attr("class", "grid-bg")
            .attr("width", endsAtPoint - startsAtPoint)
            .attr("x", startsAtPoint)
            .attr("y", 0)
            .attr("height", totalHeight)
            .attr("transform", `translate(0, -${totalHeight - rowHeight})`);
    }

    function formatTickLabels(value: Date): string {
        if (secondsPerPixel.current < 1) {
            return dayjs(value).format(DateFormats.timeSeconds);
        } else if (secondsPerPixel.current < 500) {
            return dayjs(value).format(DateFormats.time);
        } else if (secondsPerPixel.current < 700) {
            return dayjs(value).format(DateFormats.dayMonthTime);
        } else {
            return dayjs(value).format(DateFormats.dayMonthYear);
        }
    }

    function calculateCentreTime(startTime: number, endTime: number, centrePoint: number) {
        return Math.round(startTime + (endTime - startTime) * centrePoint);
    }

    function calculateDomainStart(centreTime: number, centrePoint: number) {
        return Math.round(
            domainOffset.current + centreTime - totalWidth.current * secondsPerPixel.current * 1000 * centrePoint,
        );
    }

    function calculateDomainEnd(centreTime: number, centrePoint: number) {
        return (
            domainOffset.current + centreTime + totalWidth.current * secondsPerPixel.current * 1000 * (1 - centrePoint)
        );
    }

    function calculateDomain(centrePointOverride?: number): [number, number] {
        const centrePoint = centrePointOverride ?? 0.5;

        let startTime = media.startsAt;
        let endTime = startTime + media.duration;

        // Different if user has zoomed or panned
        if (xScale.current) {
            const currentDomain = xScale.current.domain();
            startTime = currentDomain[0].getTime();
            endTime = currentDomain[1].getTime();
        }

        const centreTime = calculateCentreTime(startTime, endTime, centrePoint);

        const domainStartsAt = calculateDomainStart(centreTime, centrePoint);
        const domainEndsAt = calculateDomainEnd(centreTime, centrePoint);

        return [domainStartsAt, domainEndsAt];
    }

    function updateEntities(nextEntities: Entity[]) {
        const data = distributeRowData(
            nextEntities.map((e) => new TimelineEntityVm(e)),
            rowCount,
        );

        for (let i = 0; i < data.length; i++) {
            const entity = data[i];
            entity.dataRow = rowCount - entity.dataRow - 1;
        }

        entities.current = data;
    }

    return {
        domainOffset,
        secondsPerPixel,
        xScale,
        entities,
        drawTimeline,
        setTimelineAttributes,
        calculateDomainStart,
        calculateDomainEnd,
        calculateCentreTime,
        updateEntities,
    };
}

export default useTimeline;
